import React, { useEffect } from 'react';

import { Flex } from 'antd';
import classNames from 'classnames';

import BeamboxGlobalInteraction from '@core/app/actions/beambox/beambox-global-interaction';
import RightPanel from '@core/app/components/beambox/RightPanel';
import { ObjectPanelContextProvider } from '@core/app/components/beambox/RightPanel/contexts/ObjectPanelContext';
import SvgEditor from '@core/app/components/beambox/SvgEditor';
import RealSvgEditor from '@core/app/components/beambox/SvgEditor/RealSvgEditor';
import TopBar from '@core/app/components/beambox/TopBar';
import ImageTracePanel from '@core/app/components/dialogs/ImageTracePanel/ImageTracePanel';
import CanvasTabBar from '@core/app/components/mobile/CanvasTabBar';
import { MenuEvents, MiscEvents } from '@core/app/constants/ipcEvents';
import { CanvasProvider } from '@core/app/contexts/CanvasContext';
import { useIsMobile } from '@core/app/stores/screenStore';
import { useStorageStore } from '@core/app/stores/storageStore';
import workareaManager from '@core/app/svgedit/workarea';
import DockViewLayout from '@core/app/widgets/dockable/DockViewLayout';
import ToolBarDrawerContainer from '@core/app/widgets/dockable/ToolBarDrawerContainer';
import { hashMap } from '@core/helpers/hashHelper';
import isWeb from '@core/helpers/is-web';
import sentryHelper from '@core/helpers/sentry-helper';
import BeamboxInit from '@core/implementations/beamboxInit';
import communicator from '@core/implementations/communicator';

import styles from './Beambox.module.scss';

import 'react-resizable/css/styles.css';

sentryHelper.initSentry();

const beamboxInit = new BeamboxInit();

const Beambox = (): React.JSX.Element => {
  const isMobile = useIsMobile();

  useEffect(() => {
    window.homePage = hashMap.editor;
    communicator.send(MiscEvents.FrontendReady);
    // Init view
    workareaManager.resetView();
    beamboxInit.showStartUpDialogs();

    if (isWeb()) {
      // web never receives NewAppMenu / TabFocused, attach on mount instead
      BeamboxGlobalInteraction.attach();
    } else {
      communicator.on(MenuEvents.NewAppMenu, BeamboxGlobalInteraction.attach);
    }

    // Electron only: trap mouse/trackpad "back" (no address bar to recover from) by duplicating the
    // current entry so a pop lands on the same route, then re-arm. Web keeps native browser history.
    const armBackTrap = () => window.history.pushState(null, '', window.location.href);

    if (!isWeb()) {
      armBackTrap();
      window.addEventListener('popstate', armBackTrap);
    }

    return () => {
      BeamboxGlobalInteraction.detach();
      communicator.off(MenuEvents.NewAppMenu, BeamboxGlobalInteraction.attach);
      window.removeEventListener('popstate', armBackTrap);
    };
  }, []);

  const activeLang = useStorageStore((state) => state['active-lang']) ?? 'en';

  return (
    <CanvasProvider>
      <div className={classNames('studio-container', 'beambox-studio', activeLang, styles.container)}>
        <TopBar />
        <ObjectPanelContextProvider>
          <ToolBarDrawerContainer />
          {isMobile ? (
            <>
              <RealSvgEditor />
              <RightPanel />
              <CanvasTabBar />
            </>
          ) : (
            <Flex className={styles.main}>
              <DockViewLayout />
              {/* Note: RightPanel in non mobile mode is used to handle PanelType changes */}
              <RightPanel />
            </Flex>
          )}
          {/* This is a temp place for SvgEditor, will be moved out&in */}
          <div id="safe-editor-container" style={{ display: 'none' }}>
            <SvgEditor />
          </div>
        </ObjectPanelContextProvider>
      </div>
      <ImageTracePanel />
    </CanvasProvider>
  );
};

export default Beambox;
